import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../components/HeaderBar";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";
import AdminLayoutWrapper from "../components/AdminLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  HelpCircle,
  RefreshCw,
  Save,
  Pencil,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * Branch Registration (ASPX -> React)
 * Tables:
 * - tbl_branch_mas (dropdown)
 * - Register_mas (grid + insert + update)
 *
 * APIs:
 * GET  /api/branch-registration/branches
 * GET  /api/branch-registration/generate?branchId=
 * GET  /api/branch-registration/list?page&limit
 * POST /api/branch-registration/register
 * POST /api/branch-registration/update
 */
const BranchRegistration = () => {
  const BASE_URL = "http://nissi-india.com";

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = useMemo(() => `bg-${baseColor}-${baseShade}`, [baseColor, baseShade]);
  const hoverClass = useMemo(() => `hover:bg-${baseColor}-700`, [baseColor]);
  const navigate = useNavigate();

const handleBack = () => {
  if (saving) return;
  navigate(-1);
};

  const [showForm, setShowForm] = useState(true);

  // dropdown
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("Select");
  const [registerNo, setRegisterNo] = useState("");

  // grid
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // paging
  const recordsPerPage = 20;
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [saving, setSaving] = useState(false);

  // Optional: finyear header (if you want switching later)
  const finYear = (localStorage.getItem("finYear") || "").trim(); // ex: "2025-2026"
  const headers = useMemo(() => (finYear ? { "x-finyear": finYear } : {}), [finYear]);

  const fetchBranches = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/branch-registration/branches`, { headers });
      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load branches");
        return;
      }
      setBranches(res.data.list || []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load branches");
    }
  };

  const fetchList = async (p = 1) => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/branch-registration/list`, {
        params: { page: p, limit: recordsPerPage },
        headers,
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load list");
        return;
      }

      const list = (res.data.list || []).map((r) => ({
        ...r,
        // keep local editable states
        _isActive: !!r.ISActive,
        _allowReReg: !!r.AllowReReg,
      }));

      setRows(list);
      setPage(res.data.page || p);
      setTotalPages(res.data.totalPages || 1);
      setTotalRecords(res.data.total || 0);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
    fetchList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBranchChange = async (val) => {
    setBranchId(val);
    setRegisterNo("");

    if (!val || val === "Select") return;

    try {
      const res = await axios.get(`${BASE_URL}/api/branch-registration/generate`, {
        params: { branchId: val },
        headers,
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Branch already registered");
        setRegisterNo("");
        return;
      }

      setRegisterNo(res.data.registerNo || "");
    } catch (e) {
      console.error(e);
      toast.error("Failed to generate registration no");
    }
  };

  const validate = () => {
    if (branchId === "Select") {
      toast.error("Please Select Branch");
      return false;
    }
    if (!String(registerNo || "").trim()) {
      toast.error("Please enter Registration No");
      return false;
    }
    return true;
  };

const handleSubmit = async () => {
  if (!validate()) return;

  const getAdminId = () => {
    const direct = Number(localStorage.getItem("adminId") || 0);
    if (direct) return direct;

    try {
      const admin = JSON.parse(localStorage.getItem("admin") || "null");
      return Number(admin?.id || 0);
    } catch {
      return 0;
    }
  };

  const adminId = getAdminId();

  if (!adminId) {
    toast.error("AdminID missing! Please login again.");
    return;
  }

  try {
    setSaving(true);

    const res = await axios.post(
      `${BASE_URL}/api/branch-registration/register`,
      { branchId: Number(branchId), adminId },
      { headers }
    );

    if (!res.data?.success) {
      toast.error(res.data?.message || "Please Retry.");
      return;
    }

    toast.success(res.data?.message || "Registered Successfully.");
    setBranchId("Select");
    setRegisterNo("");
    await fetchList(1);
  } catch (e) {
    console.error(e);
    toast.error(e.response?.data?.message || "Server error");
  } finally {
    setSaving(false);
  }
};



  const toggleRow = (idx, key, checked) => {
    setRows((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, [key]: checked } : r))
    );
  };

  const handleUpdateRow = async (row, idx) => {
    try {
      const res = await axios.post(
        `${BASE_URL}/api/branch-registration/update`,
        {
          registerId: Number(row.RegisterID),
          isActive: !!row._isActive,
          allowReReg: !!row._allowReReg,
        },
        { headers }
      );

      if (!res.data?.success) {
        toast.error(res.data?.message || "Updation failed. Please retry");
        return;
      }

      toast.success(res.data?.message || "Updated Successfully.");
      await fetchList(page);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || "Server error");
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      const start = Math.max(1, page - 2);
      const end = Math.min(totalPages, start + 4);

      for (let i = start; i <= end; i++) pages.push(i);
      if (end < totalPages) pages.push("...");
    }

    return pages;
  };

  const handlePageChange = (p) => {
    if (!p || p === "..." || p === page) return;
    fetchList(p);
  };

return (
  <AdminLayoutWrapper>
    <div className={`min-h-screen ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">Branch Registration</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div
        className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : "bg-white text-black"
        }`}
      >
        {/* Header row */}
        <div className="flex justify-between items-center gap-3 flex-wrap mb-6">
          <div className="flex items-center gap-4">
            <button
      type="button"
      onClick={handleBack}
      disabled={saving}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
        isDark
          ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
          : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
      } disabled:opacity-60`}
    >
      <ChevronLeft className="w-5 h-5" />
      Back
    </button>
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Tally Branch Registration
            </h1>

            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
              }`}
            >
              {totalRecords} Records
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => window.open("/admin/help/branch-registration", "_blank")}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow hover:shadow-md transition ${
                isDark
                  ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300"
              }`}
            >
              <HelpCircle className="w-5 h-5" />
              Help
            </button>

            <button
              type="button"
              onClick={() => {
                setBranchId("Select");
                setRegisterNo("");
                fetchBranches();
                fetchList(1);
                toast.info("Refreshed");
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow hover:shadow-md transition ${
                isDark
                  ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300"
              }`}
            >
              <RefreshCw className="w-5 h-5" />
              Refresh
            </button>

            <button
              onClick={() => setShowForm(!showForm)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                isDark
                  ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
              }`}
            >
              {showForm ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              {showForm ? "Hide Entry Form" : "Show Entry Form"}
            </button>
          </div>
        </div>

        {/* Form */}
        <div className={`rounded-xl border mb-6 overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
          {showForm && (
            <div className={`${isDark ? "bg-gray-800" : "bg-gray-50"} p-6`}>
              <div className="grid grid-cols-12 gap-4 items-center max-w-4xl">
                {/* Branch */}
                <div className="col-span-12 md:col-span-6">
                  <label className="text-sm font-medium block mb-1">
                    Branch Name <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={branchId}
                    onChange={(e) => handleBranchChange(e.target.value)}
                    disabled={saving}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-black"
                    }`}
                  >
                    <option value="Select">Select</option>
                    {branches.map((b) => (
                      <option key={b.fld_branchid} value={b.fld_branchid}>
                        {b.fld_BranchName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Reg No */}
                <div className="col-span-12 md:col-span-6">
                  <label className="text-sm font-medium block mb-1">
                    Registration No <span className="text-red-500">*</span>
                  </label>
                  <input
                    value={registerNo}
                    readOnly
                    className={`w-full px-3 py-2 rounded-lg border ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-black"
                    }`}
                    placeholder="Auto generated..."
                  />
                </div>

                <div className="col-span-12 flex justify-end mt-2">
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={saving}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white disabled:opacity-60`}
                  >
                    <Save className="w-5 h-5" />
                    {saving ? "Submitting..." : "Submit"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Table */}
        <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
          <div className="overflow-x-auto max-w-full">
            <div className="max-h-[60vh] overflow-y-auto">
              <table className="min-w-[1050px] w-full">
                <thead className={`${buttonClass} text-white sticky top-0 z-10`}>
                  <tr>
                    <th className="px-3 py-3 text-center font-semibold border-r border-white/20 whitespace-nowrap">
                      S.No
                    </th>
                    <th className="px-3 py-3 text-left font-semibold border-r border-white/20 whitespace-nowrap">
                      Branch
                    </th>
                    <th className="px-3 py-3 text-left font-semibold border-r border-white/20 whitespace-nowrap">
                      Registration No
                    </th>
                    <th className="px-3 py-3 text-left font-semibold border-r border-white/20 whitespace-nowrap">
                      Registration Date
                    </th>
                    <th className="px-3 py-3 text-left font-semibold border-r border-white/20 whitespace-nowrap">
                      Activation Status
                    </th>
                    <th className="px-3 py-3 text-left font-semibold border-r border-white/20 whitespace-nowrap">
                      Activation Date
                    </th>
                    <th className="px-3 py-3 text-center font-semibold border-r border-white/20 whitespace-nowrap">
                      Is Active
                    </th>
                    <th className="px-3 py-3 text-center font-semibold border-r border-white/20 whitespace-nowrap">
                      Can Re-register
                    </th>
                    <th className="px-3 py-3 text-center font-semibold whitespace-nowrap">
                      Operation
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td colSpan={9} className="px-4 py-6 text-center opacity-70">
                        Loading...
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-4 py-6 text-center opacity-70">
                        No Registered Branches found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr
                        key={r.RegisterID}
                        className={`transition-colors duration-200 ${
                          idx % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-gray-50"
                            : isDark
                            ? "bg-gray-900"
                            : "bg-white"
                        }`}
                      >
                        <td className="px-3 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700">
                          {r.rowno}
                        </td>
                        <td className="px-3 py-3 text-sm border-r border-gray-200 dark:border-gray-700">
                          {r.BranchName}
                        </td>
                        <td className="px-3 py-3 text-sm border-r border-gray-200 dark:border-gray-700">
                          {r.RegisterNo}
                        </td>
                        <td className="px-3 py-3 text-sm border-r border-gray-200 dark:border-gray-700">
                          {r.RegisterDate ? new Date(r.RegisterDate).toLocaleDateString("en-GB") : ""}
                        </td>
                        <td className="px-3 py-3 text-sm border-r border-gray-200 dark:border-gray-700">
                          {r.IsActivatedText}
                        </td>
                        <td className="px-3 py-3 text-sm border-r border-gray-200 dark:border-gray-700">
                          {r.ActivationDate ? new Date(r.ActivationDate).toLocaleDateString("en-GB") : ""}
                        </td>

                        <td className="px-3 py-3 text-center border-r border-gray-200 dark:border-gray-700">
                          <input
                            type="checkbox"
                            checked={!!r._isActive}
                            onChange={(e) => toggleRow(idx, "_isActive", e.target.checked)}
                            className={`w-4 h-4 cursor-pointer ${isDark ? "accent-blue-400" : "accent-blue-600"}`}
                          />
                        </td>

                        <td className="px-3 py-3 text-center border-r border-gray-200 dark:border-gray-700">
                          <input
                            type="checkbox"
                            checked={!!r._allowReReg}
                            onChange={(e) => toggleRow(idx, "_allowReReg", e.target.checked)}
                            className={`w-4 h-4 cursor-pointer ${isDark ? "accent-blue-400" : "accent-blue-600"}`}
                          />
                        </td>

                        <td className="px-3 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => handleUpdateRow(r, idx)}
                            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg font-semibold shadow hover:shadow-md transition ${
                              isDark
                                ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                                : "bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300"
                            }`}
                            title="Update"
                          >
                            <Pencil className="w-4 h-4" />
                            Update
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          theme={isDark ? "dark" : "light"}
        />
      </div>
    </div>
  </AdminLayoutWrapper>
);

};

export default BranchRegistration;
